/**
 * 🧱 Refactor BT Phase 3 — section « SectionDatesChantierCreer » de CreerBonTravailDialog.jsx : JSX déplacé TEL QUEL (ex-L789-866, dédenté de 10) ;
 * variables via `useBTForm()` (BTFormProvider enrichi par `extra`). DOM, data-testid, classes, textes inchangés.
 */
import { AlertTriangle, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MiniCalendar } from '../../MiniCalendar';
import { calculerCoutEquipe, calculerHeuresBudgetees } from '../../utils';
import { useBTForm } from '../../BTFormContext';

export function SectionDatesChantierCreer() {
  const {
    allConges,
    calMonth,
    congesEquipe,
    employees,
    equipeOccupee,
    form,
    setCalMonth,
    toggleDate,
  } = useBTForm();
  return (
    <>
      {/* Dates de chantier - Mini calendrier */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4" /> Dates de Chantier ({form.dates_chantier.length} jour{form.dates_chantier.length !== 1 ? 's' : ''})</CardTitle></CardHeader>
        <CardContent>
          <MiniCalendar month={calMonth} onMonthChange={setCalMonth} selectedDates={form.dates_chantier} onToggleDate={toggleDate} equipeOccupee={equipeOccupee} congesEquipe={congesEquipe} />

          {/* ⚠️ Alerte : employé(s) assigné(s) en congé pendant la période */}
          {(() => {
            if (form.dates_chantier.length === 0 || allConges.length === 0) return null;
            const assignesIds = new Set([
              ...(form.chef_equipe_id ? [form.chef_equipe_id] : []),
              ...form.employes_assignes.map(e => e.employee_id),
            ]);
            if (assignesIds.size === 0) return null;
            const datesSet = new Set(form.dates_chantier);
            const conflits = {}; // { employee_nom: [dates conflictuelles] }
            for (const c of allConges) {
              if (!assignesIds.has(c.employee_id)) continue;
              if (!c.date_debut) continue;
              const d0 = new Date(c.date_debut + 'T12:00:00');
              const d1 = new Date((c.date_fin || c.date_debut) + 'T12:00:00');
              for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
                const ds = d.toISOString().split('T')[0];
                if (datesSet.has(ds)) {
                  const nom = c.employe_nom || c.employee_nom || 'Employé';
                  if (!conflits[nom]) conflits[nom] = [];
                  conflits[nom].push(ds);
                }
              }
            }
            const noms = Object.keys(conflits);
            if (noms.length === 0) return null;
            return (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg border-2 border-red-300 bg-red-50" data-testid="alert-employe-en-conge">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-red-600" />
                <div className="text-sm text-red-900">
                  <p className="font-bold mb-1">⚠️ Conflit de congé détecté</p>
                  <ul className="space-y-0.5 text-xs">
                    {noms.map(n => (
                      <li key={n}>
                        <strong>{n}</strong> est en congé approuvé le{conflits[n].length > 1 ? 's' : ''}{' '}
                        <span className="text-red-700">
                          {conflits[n].slice().sort().map(d => new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })).join(', ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-[11px] text-red-700 italic">Retirez cet employé ou modifiez les dates du chantier.</p>
                </div>
              </div>
            );
          })()}

          {/* Alerte jours planifiés vs estimation */}
          {(() => {
            const nbJoursPlanifies = form.dates_chantier.length;
            const heuresBudg = parseFloat(form.heures_budgetees) || 0;
            if (nbJoursPlanifies === 0 || heuresBudg === 0) return null;
            const equipe = calculerCoutEquipe(form.chef_equipe_id, employees, form.dates_chantier?.[0]);
            const nbMembres = form.employes_assignes.length > 0 ? form.employes_assignes.length : (equipe.nbMembres || 1);
            const hpj = calculerHeuresBudgetees(1, 1, 1, form.dates_chantier).heuresParJour;
            const joursEstimes = Math.ceil((heuresBudg / (hpj * nbMembres)) * 10) / 10;
            const diff = nbJoursPlanifies - joursEstimes;
            if (Math.abs(diff) < 0.5) return null;
            const manque = diff < 0;
            return (
              <div className={`mt-3 flex items-start gap-2 p-2.5 rounded-lg border text-xs ${manque ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`} data-testid="alert-jours-mismatch">
                <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${manque ? 'text-amber-500' : 'text-blue-500'}`} />
                <span>
                  <strong>{nbJoursPlanifies} jour(s) planifié(s)</strong> mais estimation de <strong>{joursEstimes} jour(s)</strong>
                  {manque ? ` — il manque ~${Math.abs(diff).toFixed(1)} jour(s) !` : ` — vous avez ~${diff.toFixed(1)} jour(s) de marge.`}
                </span>
              </div>
            );
          })()}
        </CardContent>
      </Card>

    </>
  );
}
